import './util/webpack-public-path.js';
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import 'videojs-hotkeys';

import { applyConfig } from './api/config';
import { getCurrentLanguage } from './util/localization.js';

import * as bundledPlugins from './plugins/bundled';
import * as conditionalPlugins from './plugins/conditional';
import { hotkeysConfig, isPresentationId, Cookie } from './util';
import { QueryStringParamProvider } from "./implementations/player-page/helpers.js";
import { SeekButton } from './components/seek-button.js';
import { ControlsLockButton } from './components/controls-lock-button.js';
import { fixTimeTooltipUpdate } from './plugins/bundled/seek-thumbnails.js';

import './player.css';
import './player-branding.css';
import { applyCherryPickedVideoJSFixes } from './videojs-cherry-picked-fixes.js';

const pluginNames = [];
const allPluginEntries = [
    ...Object.entries(bundledPlugins),
    ...Object.entries(conditionalPlugins)
];

const bundledPluginOptions = Object.freeze(allPluginEntries.reduce((all, [pluginName, pluginFn]) => {
    videojs.registerPlugin(pluginName, pluginFn);;
    all[pluginName] = Object.freeze({});
    pluginNames.push(pluginName);
    return all;
}, {}));
Object.freeze(pluginNames);

const _player = new WeakMap();
let localize;

/** Media player capable of playing Mediasite presentations. */
class MediasitePlayer {
    /**
     * 
     * @param {HTMLVideoElement} videoEl A video element to transform into a Mediasite player.
     * @param {Object} options
     * @param {string} options.presentationId The ID of a Mediasite presentation.
     * @param {Object<string, function>} [options.events] A dictionary of event listeners to attach to the player.
     * @param {string} [options.mediasiteRoot='/Mediasite'] The path to a Mediasite server root. The player will attempt to infer this value if it is omitted.
     * @param {Object} [options.apiConfig] Options to configure communication with Mediasite servers.
     * @param {boolean} [options.controls=true] Whether or not to display the standard controls. Set to <code>false</code> for a "chromeless" player.
     * @param {boolean} [options.fill=false] Whether or not the player should try to fill the entire container.
     * @param {boolean} [options.fluid=true] Whether or not the player height should automatically adjust to maintain the loaded video's aspect ratio.
     * @param {boolean} [options.autoplay=false] Enables autoplay on this player's HTML video element.
     * @param {number[]} [options.playbackRates=[0.5, .75, 1.0, 1.25, 1.5, 1.75, 2.0]] An array of playback rates that should be available to the user via the control bar.
     * @param {string} [options.language]
     * @param {Array} [options.hooks] An array of plugin hooks to attach to this player.
     */
    constructor(videoEl, options) {
        const { presentationId, events, mediasiteRoot, apiConfig, plugins: customPlugins = {}, controls = true, fill = false, fluid = true, autoplay = false, loop = false, playbackRates = [0.5, .75, 1.0, 1.25, 1.5, 1.75, 2.0], language = getCurrentLanguage(), lockControls = false, ...pluginOptions } = options;

        if (mediasiteRoot || apiConfig) {
            applyConfig({ mediasiteRoot, ...apiConfig });
        }

        // Apply any video.js fixes that we wanted to include instead of updating the whole thing and breaking the player
        applyCherryPickedVideoJSFixes();

        videojs.options.vhs.limitRenditionByPlayerDimensions = false;

        const player = videojs(videoEl, {
            controls,
            fill,
            fluid,
            autoplay,
            responsive: true,
            playbackRates,
            userActions: {
                hotkeys: false,
                click: this.#clickHandler, //overrides the default click + double click so that we can check the player settings to see if they are allowed to play/pause or fullscreen
                doubleClick: this.#doubleClickHandler,
            },
            liveui: true,
            crossOrigin: 'anonymous',
            language,
            plugins: bundledPluginOptions,
            html5: {
                nativeTextTracks: false,
                nativeAudioTracks: false,
                preloadTextTracks: false //don't automatically load all caption vtt files.  An asynchronous solution may be coming from videojs, but as of 12/23 it doesn't exist yet.
            },
            persistTextTrackSettings: true,
            inactivityTimeout: lockControls ? 0 : 2000,
            preferFullWindow: true
        });

        //there is a bug in the TimeTooltip class (videojs version 7.18.1) that has been fixed in a subsequent videojs version. The bug results in the video
        //thumbnails appearing outside of the video area when hovering over the far right side of the timeline. This issue is documented here: https://github.com/videojs/video.js/issues/8273
        //The following method call will override the tooltip update method and is essentially a copy of the (very minor) fix to that method that was introduced in
        //a later version of videojs (8.14+).
        fixTimeTooltipUpdate(player);

        player.collapsedButtons = [];

        localize = player.localize?.bind(player);

        player.ready(() => {
            player.layout().addChild(player.tech(true), {}, 0);
            player.hotkeys(hotkeysConfig);
            this.#modifyControlText();

            const Plugin = videojs.getPlugin('plugin');
            for (const [name, fn] of Object.entries(bundledPlugins)) {
                this[name] = Plugin.isBasic(fn) ? player[name].bind(player) : player[name]();
            }

            player.on('registercustomplugin', (event, { name, isBasic }) => {
                this[name] = isBasic ? player[name].bind(player) : player[name]();
            });

            for (const [name, fn] of Object.entries(customPlugins)) {
                player.registerPlugin(name, fn);
            }

            this.setupVolumeCooking();
            this.#setSeekingButtons(player);
            this.#addControlBarLockButton(player);
            //rather than added 'loop' to the video.js player, tell the plugin in charge of the end modal that it should loop
            player.banner().isLooping = loop;

            //handles setting up the player to allow it to successfully autoplay
            const params = new QueryStringParamProvider(location.search);
            const autoMute = params.getParam('automute')?.toLowerCase();
            if ((player.autoplay() && autoMute !== 'false') || autoMute === 'true') {
                player.muted(true);

                player.notifications().setupMuteNotification();
            }

            player.collapsedButtons?.push({
                label: localize('Picture in Picture'),
                buttonName: 'PictureInPictureToggle',
                pluginName: 'pictureInPicture',
                class: 'vjs-picture-in-picture-control'
            });

            const playRate = player.playbackRate();
            const playRateButton = player.getDescendant('ControlBar', 'PlaybackRateMenuButton');
            const playRateButtonLabel = playRateButton.el().querySelector('.vjs-playback-rate-value');
            // Next two lines are a workaround for VoiceOver not respecting aria-describedby and calling out the playback rate text
            playRateButton.el().setAttribute('aria-live', 'assertive');
            playRateButtonLabel.setAttribute('role', 'status');
            
            player.collapsedButtons?.push({
                name: 'PlaybackRateOverflowButton',
                pluginName: 'playbackRate',
                label: (playRate ?? 1) + 'x -- ' + localize('Playback Rate'),
                buttonName: 'PlaybackRateMenuButton',
                class: 'vjs-playback-rate',
                handler: function () {
                    playRateButton.menu.lockShowing();
                    playRateButton.menu.focus();
                    playRateButton.buttonPressed_ = true;
                }
            });
        });

        this.setUpOverflowMenu(player);

        player.on('ratechange', () => {
            const rate = player.playbackRate();
            const overflowButton = player.getChild('ControlBar')?.getChild('OverflowMenuButton');
            const playbackMenuItem = overflowButton?.items.find(x => x.options().iconClass === 'vjs-playback-rate');
            if (playbackMenuItem) {
                const textElement = playbackMenuItem.el().querySelector('span.vjs-menu-item-text');
                if (textElement) {
                    textElement.textContent = rate + 'x -- ' + localize('Playback Rate');
                }
            }
        });

        player.addClass('video-js');
        player.addClass('mediasite-player');
        player.getChild('ControlBar').addClass('mediasite-player__main-control-bar');
        player.getChild("ErrorDisplay").addClass('mediasite-player__modal-dialog');
        player.getDescendant('ControlBar', 'CurrentTimeDisplay').show();
        player.getDescendant('ControlBar', 'TimeDivider').show();
        player.getDescendant('ControlBar', 'DurationDisplay').show();
        // The "Volume Level" aria label is a bit too loud on Chrome with certain screen readers, so fall back on the value text (set elsewhere)
        player.getDescendant('ControlBar', 'VolumePanel').el().querySelector('.vjs-volume-bar').removeAttribute('aria-label');

        // Set up a default audio track
        const audioTrackList = player.audioTracks();
        const mainTrack = new videojs.AudioTrack({
            kind: 'main',
            label: 'English',
            language: 'en',
            enabled: true
        });
        audioTrackList.addTrack(mainTrack);

        player.loader().init(presentationId, pluginOptions);

        _player.set(this, player);
    }

    /**
     * Setups a cookie that handles a user's volume between videos
     * */
    setupVolumeCooking() {
        const player = _player.get(this);
        //setting up a cookie to keep track of the user's volume between videos
        const volumeCookie = new Cookie("MediasitePlayerOptions");
        const volumeString = "Volume";
        const muteString = "Muted";

        //if there is no value for 'Volume' this will return 'false'
        const volumeValue = volumeCookie.getValueAsNumber(volumeString, false);

        if (volumeValue) {
            player.volume(volumeValue);
        }

        const mutedValue = volumeCookie.getValueAsBool(muteString, false);

        if (mutedValue) {
            player.muted(true);
        }

        //update the cookie whenever the volume changes
        player.on('volumechange', () => {
            volumeCookie.setValue(volumeString, player.volume());
            volumeCookie.setValue(muteString, player.muted());
        });
    }

    /**
     * Adds all enabled control bar buttons to the overflow menu.  These will be hidden on larger
     * screens and shown when the screen is smaller.  The opposite will occur with the control buttons
     * themselves.  The Show More and Playback Speed buttons are excluded from this behavior.
    */
    populateMenuButtons(player) {
        const controlBarMenu = player.controlbarMenu();

        for (var i = 0; i < player.collapsedButtons?.length ?? 0; i++) {
            const buttonData = player.collapsedButtons[i];
            const pluginButton = player.getDescendant('ControlBar', buttonData.buttonName);
            if (pluginButton && !controlBarMenu.hasMenuItem(buttonData.pluginName) && !pluginButton.el().classList.contains('vjs-hidden')) {
                controlBarMenu.addMenuItem(localize(buttonData.label), {
                    name: buttonData.pluginName,
                    id: buttonData.pluginName,
                    iconClass: buttonData.class,
                    icon: buttonData.iconUrl,
                    callback: () => {
                        setTimeout(() => {
                            buttonData.handler ? buttonData.handler() : pluginButton.handleClick();
                            //fixes issue where you can't click the scrollbar for the menu on chrome
                            pluginButton.menu.el().addEventListener("mousedown", (e) => {                                
                                if (e.clientX >= e.target.clientWidth || e.clientY >= e.target.clientHeight) {
                                    e.preventDefault();
                                }
                            });
                        }, 250);
                    }
                });

                pluginButton.addClass('hide-small');
            }

            if (controlBarMenu.hasMenuItem(buttonData.pluginName) && (!pluginButton || pluginButton.el().classList.contains('vjs-hidden'))) {
                controlBarMenu.removeMenuItem(buttonData.pluginName);
            }
        }
    }

    /**
     * Hides the show more button on large screens if there are no menu items, and on small screens if
     * there are no buttons that will collapse into the menu
    */
    setShowMoreButtonVisibility(player) {
        var overflowButton = player.getDescendant('ControlBar', 'OverflowMenuButton').el();
        var overflowItems = [...overflowButton.querySelector('ul.vjs-menu-content').children];
        var flexItems = overflowItems.filter(x => x.classList.contains('flex-menu-item'));
        if (!overflowItems.length) {
            overflowButton.classList.add('hide-small');
        } else if (overflowItems.length - flexItems.length < 1) {
            overflowButton.classList.add('hide-large');
        }
    }

    setUpOverflowMenu(player) {
        player.one('playing', () => {
            this.populateMenuButtons(player);
            this.setShowMoreButtonVisibility(player);
        });
    }

    #setSeekingButtons(player) {
        const seekDuration = 10;
        const controlBar = player.getChild('ControlBar');
        const backSeekButton = new SeekButton(player, {
            seekDuration: seekDuration,
            isForward: false
        });
        const forwardSeekButton = new SeekButton(player, {
            seekDuration: seekDuration, 
            isForward: true
        });
        forwardSeekButton.addClass('hide-small');

        player.ready(() => {
            controlBar.addChild(forwardSeekButton, null, 1);
            controlBar.addChild(backSeekButton, null, 1);
        });
    }

    #addControlBarLockButton(player) {
        const controlBar = player.getChild('ControlBar');
        const button = new ControlsLockButton(player);

        player.ready(() => {
            controlBar.addChild(button, null, 0);
        });
    }

    /**
     * Overrides the default of whether we should be allowed to play/pause on clicking the video
     * @param {any} event
     */
    #clickHandler(event) {
        const params = new QueryStringParamProvider(location.search);
        const clickToPlay = params.getParam('clickToPlay')?.toLowerCase();
        if (clickToPlay === 'false') {
            return;
        }

        if (this.settings().enablePlayPause) {
            if (this.paused()) {
                this.play();
            } else {
                this.pause();
            }
        }
    }

    /**
     * Overrides the default of whether we should be allowed to toggle fullscreen 
     * @param {any} event
     */
    #doubleClickHandler(event) {
        if (this.settings().enableFullScreen) {
            if (this.isFullscreen()) {
                this.exitFullscreen();
            } else {
                this.requestFullscreen();
            }
        }        
    }

    /**
     * Updates the control text of the player to reference the hotkeys used to access them
     * If one changes the hotkeys, make sure to edit this function as well as the hotkeys.js
    */
    #modifyControlText() {
        const player = _player.get(this);

        const muteToggle = player.getDescendant('ControlBar', 'VolumePanel', 'MuteToggle');
        const playToggle = player.getDescendant('ControlBar', 'PlayToggle');

        player.on("presentationloaded", () => {
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'PlaybackRateMenuButton'), 'R');
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'ChaptersButton'), 'C + Shift');
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'AudioTrackButton'), 'A');
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'SubsCapsButton'), 'C');
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'FullscreenToggle'), 'F');
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'SwapStreamsButton'), '.');
            this.#addHotkeyToControlText(player.getDescendant('ControlBar', 'ControlsLockButton'), 'L');
            this.#addHotkeyToControlText(muteToggle, 'M');
        });

        player.on('play', () => {
            this.#addHotkeyToControlText(playToggle, 'Spacebar');
        });
        
        player.on('pause', () => {
            this.#addHotkeyToControlText(playToggle, 'Spacebar');
        });

        player.on('volumechange', () => {
            this.#addHotkeyToControlText(muteToggle, 'M');
        });
    }

    /**
     * A function for formatting an element's control text
     * @param {Component} element
     * @param {string} key
     */
    #addHotkeyToControlText(element, key) {
        if (!element) {
            return;
        }

        const controlText = element.controlText();
        if (controlText.slice(-3) !== `(${key})`) {
            element.controlText(`${controlText} (${key})`);
        }
    }


    /**
     * Begins playback.
     * @returns {Promise} A Promise that is resolved once playback has actually started, or rejected if enablePlayPause has been disabled in the player settings.
     */
    play() {
        if (_player.get(this).settings?.().enablePlayPause) {
            return isPresentationId(_player.get(this).loader().presentationId) ? _player.get(this).play() : Promise.resolve();
        }
        else {
            return Promise.reject(Error('Unable to play: play/pause functions are currently disabled.'));
        }
    }

    /** Pauses playback. */
    pause() {
        if (_player.get(this).settings?.().enablePlayPause) {
            return _player.get(this).pause();
        }
        else {
            _player.get(this).log('Attempted to pause, but pausing is disabled in the UI.');
            throw Error('Unable to pause: play/pause functions are currently disabled.');
        }
    }

    /**
     * Whether or not playback is currently paused.
     * @returns {boolean}
     */
    paused() {
        return _player.get(this).paused();
    }

    /**
     * Seeks the player to the given time in seconds, or returns the current time.
     * @param {number} [seconds] The time in seconds to which to seek. If omitted, the player's current time will be returned and no seeking will be performed.
     */
    currentTime(seconds) {
        if (seconds === undefined || _player.get(this).settings?.().enableSeeking) {
            return (isPresentationId(_player.get(this).loader().presentationId) || seconds === undefined) ? _player.get(this).currentTime(seconds) : 0;
        }
        else {
            _player.get(this).log('Attempted to seek, but seeking is disabled in the UI.');
            throw Error('Unable to seek: seek function is currently disabled.');
        }
    }

    /**
     * Requests the player to open in full screen mode. Note: in modern browsers this only works if called directly from an event handler for a user gesture.
     * @param {FullscreenOptions} options
     */
    requestFullscreen(options) {
        if (_player.get(this).settings?.().enableFullScreen) {
            return isPresentationId(_player.get(this).loader().presentationId) ? _player.get(this).requestFullscreen(options) : null;
        }
        else {
            _player.get(this).log('Attempted to enter full screen, but full screen mode is disabled.');
            throw Error('Unable to enter full screen: full screen function is currently disabled.');
        }
    }

    /**
     * Sets the player's playback rate, or returns the current rate.
     * @param {number} [rate] The playback rate. If omitted, the player's current playback rate will be returned without modification.
     * @returns {number|undefined} The current playback rate when getting, or undefined when setting.
     */
    playbackRate(rate) {
        if (rate === undefined || _player.get(this).settings?.().enableSpeedControl) {
            return _player.get(this).playbackRate(rate);
        }
        else {
            _player.get(this).log('Attempted to set the playback rate, but changing playback rates is disabled.');
            throw Error('Unable to set playback rate: changing the playback rate is currently disabled.');
        }
    }

    /**
     * Activates or deactivates the related plugins that change their functionality when seeking is enabled or not
     * @param {boolean} enableSeeking
     */
    enableSeeking(enableSeeking) {
        if (_player.usingPlugin('quizzes')) {
            _player.get(this).quizzes().enableSeeking = enableSeeking;
        }

        _player.get(this).settings().enableSeeking = enableSeeking;
        _player.get(this).chapters().showChapterList = enableSeeking;
        _player.get(this).search().enabled = enableSeeking;
    }

    /**
     * Returns the total duration of the media in seconds.
     * @returns {number}
     */
    duration() { return _player.get(this).duration() }

    /**
     * Sets the player volume to the given value, or returns the current volume.
     * @param {number} [newValue] A volume level between 0.0 and 1.0. If omitted, the player's current volume will be returned without modification.
     * @returns {number|undefined} The current volume when getting, or undefined when setting.
     */
    volume(newValue) { return _player.get(this).volume(newValue) }

    /**
     * Mutes/unmutes the player, or returns a boolean indicating whether the player is currently muted.
     * @param {boolean} [newValue] Whether or not the player should be muted. If omitted, a boolean will be returned indicating whether or not the player is currently muted.
     * @returns {boolean|undefined} Whether or not the player is currently muted when getting, or undefined when setting.
     */
    muted(newValue) { return _player.get(this).muted(newValue) }

    /**
     * Attaches an event listener for the given event type.
     * @param {string|string[]} type The type of event for which to listen, or an array of event types.
     * @param {function(Event)} fn The handler to call when the event occurs.
     */
    on(type, fn) { return _player.get(this).on(type, fn) }

    /** 
     * Detaches a previously registered event listener.
     * @param {string|string[]} type The type of event listener to remove.
     * @param {function(Event)} fn A reference to the function that was previously registered as an event listener for this event.
     */
    off(type, fn) { return _player.get(this).off(type, fn) }

    /**
     * Registers an event listener that will only fire once, the first time the event occurs.
     * @param {string|string[]} type The type of event for which to listen, or an array of event types.
     * @param {function(Event)} fn The handler to call when the event occurs.
     */
    one(type, fn) { return _player.get(this).one(type, fn) }

    /** Gracefully destroys this player instance and releases any resources it is using. */
    dispose() { return _player.get(this).dispose() }

    //Dynamically loads an external JavaScript file containing one or more custom plugins.<br><br>
    //@param { string } url The path to a JavaScript module which exposes one or more plugin wrapper functions as named exports.The name of each export found in the module will be used as the name of the plugin when registering it with the player.

    registerExternalPlugin(url) { return _player.get(this).registerExternalPlugin(url) }

    /**
     * A reference to the Video.js library itself.
     * @type {videojs}
     */
    get videojs () {
        return videojs;
    }
}

/**
 * The base Plugin class from Video.js.
 * @external "videojs.Plugin"
 * @see {@link https://docs.videojs.com/plugin}
 */

export default MediasitePlayer;
export { pluginNames };
export { default as videojs } from 'video.js';
export { Config } from './api';
